import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaTrash } from "react-icons/fa";

import {
  deletePluviometerStation,
  updatePluviometerReading,
  updatePluviometerStation,
} from "../../utils/farmPluviometryApi";
import FarmMapDeleteConfirmModal from "./FarmMapDeleteConfirmModal";

function toLocalInputValue(isoValue) {
  if (!isoValue) return "";
  const date = new Date(isoValue);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (value) => String(value).padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

function fromLocalInputValue(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

function PluviometerEditModal({ gauge, farmId, onClose, onSaved, onDeleted }) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState("");
  const [name, setName] = useState(gauge.label || "");
  const [stationNumber, setStationNumber] = useState(gauge.stationNumber || "");
  const [rainMm, setRainMm] = useState(String(gauge.mm != null ? gauge.mm : ""));
  const [periodStart, setPeriodStart] = useState(toLocalInputValue(gauge.periodStart));
  const [periodEnd, setPeriodEnd] = useState(toLocalInputValue(gauge.periodEnd));

  useEffect(() => {
    setName(gauge.label || "");
    setStationNumber(gauge.stationNumber || "");
    setRainMm(String(gauge.mm != null ? gauge.mm : ""));
    setPeriodStart(toLocalInputValue(gauge.periodStart));
    setPeriodEnd(toLocalInputValue(gauge.periodEnd));
    setError("");
  }, [gauge]);

  async function handleSubmit(event) {
    event.preventDefault();
    setSaving(true);
    setError("");

    try {
      await updatePluviometerStation(farmId, gauge.stationUuid, {
        name: name.trim(),
        stationNumber: stationNumber.trim(),
      });

      const readingPayload = {
        rainMm: Number(rainMm) || 0,
      };
      const startIso = fromLocalInputValue(periodStart);
      const endIso = fromLocalInputValue(periodEnd);
      if (startIso) readingPayload.periodStart = startIso;
      if (endIso) readingPayload.periodEnd = endIso;

      const result = await updatePluviometerReading(
        farmId,
        gauge.readingUuid,
        readingPayload
      );

      onSaved({
        ...gauge,
        label: result.station.name,
        stationNumber: result.station.stationNumber,
        mm: result.reading.rainMm,
        periodStart: result.reading.periodStart,
        periodEnd: result.reading.periodEnd,
      });
      onClose();
    } catch (submitError) {
      setError(
        t("Não foi possível salvar o pluviômetro. Tente novamente.")
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!gauge.stationUuid) {
      return;
    }

    setDeleting(true);
    setError("");
    try {
      await deletePluviometerStation(farmId, gauge.stationUuid);
      setShowDeleteConfirm(false);
      if (onDeleted) {
        onDeleted(gauge);
      }
      onClose();
    } catch (deleteError) {
      setError(t("Não foi possível excluir o pluviômetro."));
    } finally {
      setDeleting(false);
    }
  }

  const busy = saving || deleting;

  return (
    <>
    <div className="farm-pluviometer-modal">
      <div
        className="farm-pluviometer-modal__backdrop"
        onClick={busy ? undefined : onClose}
      />
      <form className="farm-pluviometer-modal__panel" onSubmit={handleSubmit}>
        <h3>{t("Editar pluviômetro")}</h3>
        <p className="farm-pluviometer-modal__hint">
          {t(
            "Ajuste nome, número e leitura de chuva. Alterações feitas aqui ficam registradas pela plataforma web."
          )}
        </p>

        <label>
          {t("Nome")}
          <input
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
          />
        </label>

        <label>
          {t("Número")}
          <input
            type="text"
            value={stationNumber}
            onChange={(event) => setStationNumber(event.target.value)}
          />
        </label>

        <label>
          {t("Chuva (mm)")}
          <input
            type="number"
            min="0"
            step="0.1"
            value={rainMm}
            onChange={(event) => setRainMm(event.target.value)}
            required
          />
        </label>

        <label>
          {t("Início do período")}
          <input
            type="datetime-local"
            value={periodStart}
            onChange={(event) => setPeriodStart(event.target.value)}
          />
        </label>

        <label>
          {t("Fim do período")}
          <input
            type="datetime-local"
            value={periodEnd}
            onChange={(event) => setPeriodEnd(event.target.value)}
          />
        </label>

        {error ? <div className="farm-pluviometer-modal__error">{error}</div> : null}

        <div className="farm-pluviometer-modal__actions">
          <button
            type="button"
            className="btn btn-sm btn-default"
            onClick={onClose}
            disabled={busy}
          >
            {t("Cancelar")}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-danger farm-pluviometer-modal__delete-btn"
            onClick={() => setShowDeleteConfirm(true)}
            disabled={busy}
            title={t("Excluir pluviômetro")}
            aria-label={t("Excluir pluviômetro")}
          >
            <FaTrash aria-hidden />
          </button>
          <button type="submit" className="btn btn-sm btn-primary" disabled={busy}>
            {saving ? t("Salvando...") : t("Salvar")}
          </button>
        </div>
      </form>
    </div>

    <FarmMapDeleteConfirmModal
      open={showDeleteConfirm}
      title={t("Excluir pluviômetro")}
      message={t(
        "Tem certeza de que deseja excluir o pluviômetro {{name}}? Ele deixará de aparecer no mapa e no histórico de chuva.",
        { name: gauge.label || t("Pluviômetro") }
      )}
      confirmLabel={t("Excluir pluviômetro")}
      cancelLabel={t("Cancelar")}
      loading={deleting}
      onConfirm={handleDelete}
      onCancel={() => {
        if (!deleting) {
          setShowDeleteConfirm(false);
        }
      }}
    />
    </>
  );
}

export default PluviometerEditModal;
